import { useState, useEffect } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { Menu, X, Phone } from 'lucide-react';

export default function Navbar() {
  const { t, lang, toggleLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const links = [
    { key: 'nav_services', href: '#servicos' },
    { key: 'nav_portfolio', href: '#portfolio' },
    { key: 'nav_about', href: '#sobre' },
    { key: 'nav_contact', href: '#contacto' },
  ];

  const scrollTo = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-xl shadow-sm border-b border-zinc-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <button
            onClick={() => scrollTo('#inicio')}
            className="flex-shrink-0 focus:outline-none"
            data-testid="nav-logo"
          >
            <span className="font-bold text-xl text-zinc-900 tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              RJ <span className="text-[#C2410C]">Montagens</span>
            </span>
          </button>

          <div className="hidden md:flex items-center gap-8">
            {links.map((link) => (
              <button
                key={link.key}
                onClick={() => scrollTo(link.href)}
                className="text-zinc-600 hover:text-zinc-900 text-sm font-medium transition-colors duration-200"
                data-testid={`nav-link-${link.key}`}
              >
                {t(link.key)}
              </button>
            ))}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+351915995141"
              className="hidden lg:flex items-center gap-1.5 text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
              data-testid="nav-phone-link"
            >
              <Phone size={14} />
              +351 915 995 141
            </a>
            <button
              onClick={toggleLang}
              className="text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-zinc-900 px-3 py-1.5 rounded-full border border-zinc-200 hover:border-zinc-400 transition-all duration-200"
              data-testid="lang-toggle"
            >
              {lang === 'pt' ? 'ES' : 'PT'}
            </button>
            <button
              onClick={() => scrollTo('#contacto')}
              className="px-5 py-2.5 bg-[#C2410C] hover:bg-[#9A3412] text-white text-sm font-semibold rounded-full transition-all duration-200 active:scale-95 shadow-sm"
              data-testid="nav-cta-btn"
            >
              {t('nav_cta')}
            </button>
          </div>

          <button
            className="md:hidden p-2 rounded-lg text-zinc-700 hover:bg-zinc-100 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-zinc-100 shadow-lg">
          <div className="px-4 py-5 space-y-1">
            {links.map((link) => (
              <button
                key={link.key}
                onClick={() => scrollTo(link.href)}
                className="flex w-full text-left px-3 py-2.5 text-zinc-700 font-medium rounded-lg hover:bg-zinc-50 transition-colors"
              >
                {t(link.key)}
              </button>
            ))}
            <a
              href="tel:+351915995141"
              className="flex w-full items-center justify-center gap-2 py-2.5 bg-[#C2410C]/10 text-[#C2410C] rounded-full text-sm font-semibold"
              data-testid="nav-phone-mobile"
            >
              <Phone size={14} />
              +351 915 995 141
            </a>
            <div className="flex gap-3 pt-3 border-t border-zinc-100 mt-3">
              <button
                onClick={toggleLang}
                className="flex-1 py-2 border border-zinc-200 rounded-full text-sm font-semibold text-zinc-600 hover:border-zinc-400 transition-colors"
                data-testid="lang-toggle-mobile"
              >
                {lang === 'pt' ? 'Español' : 'Português'}
              </button>
              <button
                onClick={() => scrollTo('#contacto')}
                className="flex-1 py-2 bg-[#C2410C] text-white rounded-full text-sm font-semibold"
                data-testid="nav-cta-mobile"
              >
                {t('nav_cta')}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
